import type { MsrIncoTerm, MsrPurchaseOrderInput } from '~/graphql/graphql';
import { getLocalDateString } from '~/utils';
import { optionalEmployeeIdSchema } from '~/utils/employeeId';

export const MAX_DAYS_INTRANSIT = 10_000;
export const MAX_INCO_TERMS_LOC_LENGTH = 255;

export interface MsrPurchaseOrderInputsForm {
  /** undefined until the buyer picks one, which keeps the default (their own code). */
  buyerCode?: string;
  orderedByCode: string;
  cashContract: boolean;
  numDaysIntransit: string;
  /** '' keeps the vehicle's default unit type. */
  unitType: string;
  isImport: boolean;
  readyAtMill: string;
  /** '' keeps the order's own incoterm. */
  incoTerms: MsrIncoTerm | '';
  /** '' takes the incoterm's default location. */
  incoTermsLoc: string;
}

export const INITIAL_MSR_PURCHASE_ORDER_INPUTS: MsrPurchaseOrderInputsForm = {
  orderedByCode: '',
  cashContract: false,
  numDaysIntransit: '',
  unitType: '',
  isImport: false,
  readyAtMill: '',
  incoTerms: '',
  incoTermsLoc: '',
};

export type MsrInputsErrors = Partial<{
  buyerCode: 'required' | 'too_long';
  orderedByCode: 'too_long';
  numDaysIntransit: 'invalid' | 'too_large';
  readyAtMill: 'in_past';
  incoTermsLoc: 'too_long';
}>;

export function effectiveBuyerCode(
  form: MsrPurchaseOrderInputsForm,
  selfEmployeeId: string | undefined
): string {
  return form.buyerCode ?? selfEmployeeId ?? '';
}

function daysError(days: string): MsrInputsErrors['numDaysIntransit'] {
  const text = days.trim();
  if (text === '') return undefined;
  if (!/^\d+$/.test(text)) return 'invalid';
  return Number(text) > MAX_DAYS_INTRANSIT ? 'too_large' : undefined;
}

export function msrInputsErrors(
  form: MsrPurchaseOrderInputsForm,
  selfEmployeeId: string | undefined
): MsrInputsErrors {
  const buyerCode = effectiveBuyerCode(form, selfEmployeeId);
  const errors: MsrInputsErrors = {};
  if (buyerCode.trim() === '') {
    errors.buyerCode = 'required';
  } else if (!optionalEmployeeIdSchema.safeParse(buyerCode).success) {
    errors.buyerCode = 'too_long';
  }
  if (!optionalEmployeeIdSchema.safeParse(form.orderedByCode).success) {
    errors.orderedByCode = 'too_long';
  }
  const days = daysError(form.numDaysIntransit);
  if (days) errors.numDaysIntransit = days;
  if (form.readyAtMill !== '' && form.readyAtMill < getLocalDateString()) {
    errors.readyAtMill = 'in_past';
  }
  if (
    form.isImport &&
    form.incoTermsLoc.trim().length > MAX_INCO_TERMS_LOC_LENGTH
  ) {
    errors.incoTermsLoc = 'too_long';
  }
  return errors;
}

export type MsrInputsIssue = 'buyer_required' | 'invalid';

/** Why Place Order is held back, or undefined when the inputs can be sent. */
export function msrInputsIssue(
  form: MsrPurchaseOrderInputsForm,
  selfEmployeeId: string | undefined
): MsrInputsIssue | undefined {
  const errors = msrInputsErrors(form, selfEmployeeId);
  if (errors.buyerCode === 'required') return 'buyer_required';
  return Object.keys(errors).length === 0 ? undefined : 'invalid';
}

export function toMsrPurchaseOrderInput(
  form: MsrPurchaseOrderInputsForm,
  selfEmployeeId: string | undefined
): MsrPurchaseOrderInput {
  const days = form.numDaysIntransit.trim();
  return {
    buyerCode: effectiveBuyerCode(form, selfEmployeeId).trim() || null,
    orderedByCode: form.orderedByCode.trim() || null,
    cashContract: form.cashContract,
    numDaysIntransit: days === '' ? null : Number(days),
    unitType: form.unitType.trim() || null,
    isImport: form.isImport,
    readyAtMill: form.readyAtMill || null,
    incoTerms: (form.isImport && form.incoTerms) || null,
    incoTermsLoc: (form.isImport && form.incoTermsLoc.trim()) || null,
  };
}
